/**
 * Módulo del tutorial: Balanza Bluetooth (KAN-232).
 *
 * Conectar la balanza digital (Tru-Test/EziWeigh, u otra con salida
 * serial/Bluetooth) una sola vez y el peso cae solo en la ficha del animal
 * — animal por animal en la manga, sin tipear. Y de yapa: los lectores de
 * caravanas HID se detectan solos, sin conectar nada.
 *
 * Anclas: animals-btn-nueva-sesion (toolbar de Mi Ganado),
 * animal-connect-scale + animal-hid-hint (sección 📡 del formulario).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { sesionAbiertaConAnimales } from '../../../services/tutorial/sessionCondition';

export const balanzaModule: TutorialModuleDef = {
  id: 'balanza',
  title: 'Balanza sin tipear',
  description: 'Conectá la balanza una vez y el peso cae solo en la ficha de cada animal — y el bastón lector de caravanas se detecta solo.',
  emoji: '⚖️',
  image: 'images/tutorial/balanza.png',
  permission: { module: 'animals', action: 'create' },
  grupo: 'produccion',
  route: '/animals/new',
  steps: [
    {
      id: 'concepto-balanza',
      route: '/tutorial',
      text: 'En la manga, cada segundo cuenta: conectás la balanza una vez y el peso cae SOLO en la ficha del animal. Adiós tipear kilos con el guante puesto.',
      narration:
        'Ahora te ahorro el trabajo más pesado de la manga: anotar pesos. Conectás la balanza digital una sola vez, y de ahí en adelante el peso cae solo en la ficha de cada animal, sin tipear nada. Vos te ocupás del animal, la app del número. Quedate que te muestro dónde se conecta.',
    },
    {
      id: 'ir-a-nueva-sesion',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-nueva-sesion"]',
      label: 'Nueva Sesión (en Mi Ganado)',
      text: 'La balanza se conecta desde la carga de animales: en "Mi Ganado" (menú Ganado), tocá "Nueva Sesión" (o abrí cualquier animal).',
      narration:
        'La conexión vive en el formulario del animal, así que arrancamos como siempre: andá a Mi Ganado, en el menú sección Ganado, y tocá Nueva Sesión. Si preferís, también sirve abriendo cualquier animal ya cargado — el formulario es el mismo.',
    },
    {
      // Paso condicional: sólo aparece si el usuario ya tenía una sesión de
      // carga abierta con animales (las sesiones quedan abiertas hasta que
      // se inicia una nueva — SessionContext persiste en localStorage).
      id: 'sesion-abierta-balanza',
      route: '/animals/new',
      target: '[data-ar-guide="sesion-modal-nueva"]',
      label: 'Cerrá la sesión anterior',
      showIf: sesionAbiertaConAnimales,
      text: '¡Ojo! Tenés una sesión ABIERTA de la última vez (las sesiones quedan abiertas hasta que iniciás una nueva). Tocá "Nueva Sesión" acá para cerrar esa y arrancar una limpia.',
      narration:
        'Frená un segundo: tenés una sesión abierta de la última vez. No se perdió nada, es que las sesiones de esta app quedan abiertas hasta que vos iniciás una nueva, aunque cierres el navegador. Tocá Nueva Sesión, el botón colorado de este aviso: la vieja queda guardada, y arrancamos una limpia para conectar la balanza.',
    },
    {
      id: 'selector-carga-balanza',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Comenzar a cargar',
      text: 'Como en toda sesión: marcá "Peso" (o usá "Carga rápida") y tocá "Comenzar a cargar" — la sección de la balanza te espera más abajo en el formulario.',
      narration:
        'Como en toda carga: primero elegí los datos de la sesión, marcá el Peso si no está, o de una Carga rápida, y apretá Comenzar a cargar. La sección que buscamos queda más abajo, adentro del formulario.',
    },
    {
      id: 'seccion-balanza',
      route: '/animals/new',
      target: '[data-ar-guide="animal-connect-scale"]',
      advanceTargets: ['[data-ar-guide="animal-connect-scale-mobile"]'],
      label: '📡 Conectar Balanza',
      text: 'Acá está: la sección "📡 Balanza Digital". Tocá "Conectar Balanza" y elegí tu balanza (Tru-Test, EziWeigh…) de la lista que te ofrece el navegador.',
      narration:
        'Bajá en el formulario hasta la sección del platito con ondas: Balanza Digital. Tocá Conectar Balanza, y el navegador te va a mostrar los dispositivos cerca: elegí la tuya, Tru-Test, EziWeigh, la que sea. Un toque y queda amigada para toda la carga.',
    },
    {
      id: 'donde-cae-el-peso',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-peso"]',
      label: 'El campo donde cae el peso',
      advanceOnClick: false,
      text: '¿Y a dónde va a parar el peso? A este campo: cada pesaje estable de la balanza aparece acá solo, sin que lo toques.',
      narration:
        '¿Y a dónde cae el peso? A este campito de acá. Cada vez que la balanza manda un pesaje estable, el número aparece acá solito, sin que lo toques. Vos mirás que haya entrado bien y a lo próximo.',
    },
    {
      id: 'el-peso-cae-solo',
      route: '/animals/new',
      text: 'Conectada, cada peso estable que manda la balanza aparece solo en el campo de peso de la ficha. Vos pasás el animal, mirás que haya entrado, y seguís.',
      narration:
        'Y ahora la magia: cada vez que la balanza manda un peso estable, el número aparece solo en el campo de peso de la ficha. Vos pasás el animal, de un vistazo ves que el kilaje entró, cargás la caravana y al siguiente. En Android, emparejá la balanza primero desde los ajustes de Bluetooth del teléfono.',
    },
    {
      id: 'hid-y-cierre',
      route: '/animals/new',
      target: '[data-ar-guide="animal-hid-hint"]',
      label: 'Bastón lector (aclaración)',
      advanceOnClick: false,
      text: '¿Usás bastón lector de caravanas? Mejor todavía: no se conecta — se detecta solo (vinculalo por Bluetooth una vez). Balanza y bastón: la manga completa sin teclado.',
      narration:
        'Y un regalo para los que usan lector de caravanas electrónico: ese no se conecta nunca, se detecta solo, como si fuera un teclado. Vinculalo una vez por Bluetooth y listo: pasás la caravana por el bastón y el número aparece escrito. Balanza por un lado, bastón por el otro, y el teclado de sobra.',
    },
    {
      id: 'cerrar-sesion-balanza',
      route: '/animals/new',
      target: '[data-ar-guide="animal-session-salir"]',
      label: 'Salir (cerrar la sesión)',
      text: 'Cerrá la jornada con "Salir" cuando termines — la sesión queda guardada con todos sus pesos.',
      narration:
        'Y cuando se termina la manga, cerrá la jornada con Salir: la sesión queda guardada con todos sus pesos, ordenadita. Balanza amigada, carga volada.',
    },
  ],
  completionCheck: async () => false, // no hay estado persistido de balanza conectada
};
